import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { useAuth } from '../Authorization/AuthContext';

function useApi() {
  const { getValidAccessToken, selectedProject } = useAuth();

  const getBaseUrl = () => {
    return process.env.FAST_APP_API_BASE_URL || API_BASE_URL; // put in to use the environment variable for when we deploy to Azure
  };

  const getAuthHeaders = async (data?: unknown) => {
    const token = await getValidAccessToken();
    const baseHeaders: Record<string, string> = {};
    
    // Only set Content-Type for non-FormData requests
    // FormData requires the browser to set Content-Type with boundary
    if (!(data instanceof FormData)) {
      baseHeaders["Content-Type"] = "application/json";
    }
    
    return {
      ...baseHeaders,
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
  };

  async function get<R>(path: string, includeDb: boolean = true) {
    const baseUrlFromEnv = getBaseUrl();
    const baseUrl = (includeDb && selectedProject) ? `${baseUrlFromEnv}/${selectedProject.dbname}` : baseUrlFromEnv;
    return axios.get<R>(`${baseUrl}/${path}`, { headers: await getAuthHeaders() });
  }
  
  async function post<R, D = unknown>(path: string, data?: D, includeDb: boolean = true) {
    const baseUrlFromEnv = getBaseUrl();
    const baseUrl = (includeDb && selectedProject) ? `${baseUrlFromEnv}/${selectedProject.dbname}` : baseUrlFromEnv;
    return axios.post<R>(`${baseUrl}/${path}`, data, { headers: await getAuthHeaders(data) });
  }

  async function patch<R, D = unknown>(path: string, data?: D, includeDb: boolean = true) {
    const baseUrlFromEnv = getBaseUrl();
    const baseUrl = (includeDb && selectedProject) ? `${baseUrlFromEnv}/${selectedProject.dbname}` : baseUrlFromEnv;
    return axios.patch<R>(`${baseUrl}/${path}`, data, { headers: await getAuthHeaders(data) });
  }

  async function remove<R>(path: string, includeDb: boolean = true) {
    const baseUrlFromEnv = getBaseUrl();
    const baseUrl = (includeDb && selectedProject) ? `${baseUrlFromEnv}/${selectedProject.dbname}` : baseUrlFromEnv;
    return axios.delete<R>(`${baseUrl}/${path}`, { headers: await getAuthHeaders() });
  }

  // Method for downloading blob data (files, attachments, etc.)
  async function getBlob(path: string, includeDb: boolean = true) {
    const baseUrlFromEnv = getBaseUrl();
    const baseUrl = (includeDb && selectedProject) ? `${baseUrlFromEnv}/${selectedProject.dbname}` : baseUrlFromEnv;
    return axios.get(`${baseUrl}/${path}`, { 
      headers: await getAuthHeaders(),
      responseType: 'blob'
    });
  }

  // Utility for streaming fetch in browser, now as a method in useApi
  async function fetchStream(url: string, options: RequestInit = {}) {
    const token = await getValidAccessToken();
    options.headers = {
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
    const baseUrlFromEnv = getBaseUrl();
    const baseUrl = selectedProject ? `${baseUrlFromEnv}/${selectedProject.dbname}` : baseUrlFromEnv;
    const response = await fetch(`${baseUrl}/${url}`, options);
    if (!response.body) throw new Error('ReadableStream not supported in this browser.');
    return response.body.getReader();
  }

  return { get, post, patch, remove, getBlob, fetchStream };
}

export default useApi;

